import {
  useQuery,
  useMutation,
  UseMutationResult,
  UseQueryResult,
  UseQueryOptions,
  UseMutationOptions,
} from 'react-query';
import { AIFillAppAPI } from 'api/ai-fill';
import { AiFillRuntimeInput } from 'api/ai-fill/types';
import { StatusUpdateId } from 'api/models';
import {
  StatusUpdate,
  StatusUpdatesAPI,
  StatusUpdateUpdatePayload,
} from 'api/status-updates';
import { tenMinutes } from './constants';

export const getReadStatusUpdatesDetailQueryKey = (
  statusUpdateId: StatusUpdateId,
): (string | number)[] => ['status_update_read_query', ...statusUpdateId];

export const getStatusUpdatesDetailQueryKey = (
  statusUpdateId: StatusUpdateId,
): (string | number)[] => ['status_update_query', ...statusUpdateId];

export const useUpdateStatusUpdateMutation = (
  id: number,
): UseMutationResult<StatusUpdate, unknown, StatusUpdateUpdatePayload> => {
  return useMutation<StatusUpdate, unknown, StatusUpdateUpdatePayload>(
    (payload) => StatusUpdatesAPI.update(id, payload),
  );
};

export const usePublishStatusUpdateMutation = (): UseMutationResult<
  StatusUpdate,
  unknown,
  number
> => {
  return useMutation<StatusUpdate, unknown, number>((id: number) =>
    StatusUpdatesAPI.publish(id),
  );
};

export const useGetStatusUpdateDetailsQuery = (
  statusUpdateId: StatusUpdateId,
  options?: UseQueryOptions<
    StatusUpdate,
    unknown,
    StatusUpdate,
    (string | number)[]
  >,
): UseQueryResult<StatusUpdate, unknown> => {
  return useQuery(
    getStatusUpdatesDetailQueryKey(statusUpdateId),
    () => {
      return StatusUpdatesAPI.getStatusUpdateDetails(statusUpdateId).then(
        (response) => {
          if (response?.publishedData) {
            const publishedData = JSON.parse(
              (response?.publishedData as unknown as string) ?? '',
            );

            return { ...response, publishedData };
          }

          return response;
        },
      );
    },
    {
      ...options,
      staleTime: tenMinutes,
      cacheTime: 0,
    },
  );
};

export const useGetStatusUpdateReadStatusQuery = (
  statusUpdateId: StatusUpdateId,
  options: UseQueryOptions<
    StatusUpdate,
    unknown,
    StatusUpdate,
    (string | number)[]
  >,
): UseQueryResult<StatusUpdate, unknown> => {
  return useQuery(
    getReadStatusUpdatesDetailQueryKey(statusUpdateId),
    () => StatusUpdatesAPI.setStatusUpdateAsUnread(statusUpdateId),
    options,
  );
};

export const useMutationResolveAiFillStatusUpdate = (
  statusUpdateId: number,
  options?: UseMutationOptions<void, unknown, AiFillRuntimeInput, unknown>,
): UseMutationResult<void, unknown, AiFillRuntimeInput> => {
  return useMutation(
    ['resolve_ai_fill_status_update', statusUpdateId],
    (aiFillRuntimeInput: AiFillRuntimeInput) =>
      AIFillAppAPI.aiFillResolveStatusUpdate(
        statusUpdateId,
        aiFillRuntimeInput,
      ),
    options,
  );
};
